import type { Rating } from "@/util/fsrs";
import { deepReadonly } from "@pinyinly/lib/collections";
import type { IsEqual } from "@pinyinly/lib/types";
import type { Interval } from "date-fns";
import type { DeepReadonly } from "ts-essentials";
import { z } from "zod";

const isString = (x: unknown): x is string => typeof x === `string`;

export type Skill =
  | DeprecatedSkill
  | HanziWordSkill
  | CharacterSkill
  | PinyinInitialAssociationSkill
  | PinyinFinalAssociationSkill;

export type DeprecatedSkill =
  | (string & z.$brand<`DeprecatedSkill`>)
  | `${`xx` | `re` | `er` | `rp` | `pr`}:${string}:${string}`;

export type HanziWordToGlossTypedSkill = `het:${string}:${string}`;
export type HanziWordToPinyinTypedSkill = `hp:${string}:${string}`;
export type HanziWordToPinyinInitialSkill = `hpi:${string}:${string}`;
export type HanziWordToPinyinToneSkill = `hpt:${string}:${string}`;
export type HanziWordToPinyinFinalSkill = `hpf:${string}:${string}`;
export type HanziCharacterWritingSkill = `hwr:${HanziCharacter}`;

export type HanziWordSkill =
  | (string & z.$brand<`HanziWordSkill`>)
  | HanziWordToGlossTypedSkill
  | HanziWordToPinyinTypedSkill
  | HanziWordToPinyinInitialSkill
  | HanziWordToPinyinToneSkill
  | HanziWordToPinyinFinalSkill
  | `${`he` | `eh` | `ph` | `ih`}:${string}:${string}`;

export type CharacterSkill = HanziCharacterWritingSkill;

export type PinyinInitialAssociationSkill =
  | (string & z.$brand<`PinyinInitialAssociationSkill`>)
  | `pia:${string}:${string}`;

export type PinyinFinalAssociationSkill =
  | (string & z.$brand<`PinyinFinalAssociationSkill`>)
  | `pfa:${string}:${string}`;

/**
 * A static ID for the different components/atoms of a pinyin sound, i.e. the
 * initial, final, and tone.
 *
 * - Initials: suffixed with `-`.
 * - Finals: prefixed with '-`.
 * - Tones: Single digit between 1 and 5, inclusive.
 */
export type PinyinSoundId = string & z.$brand<`PinyinSoundId`>;
export const pinyinSoundIdSchema = z
  .string()
  .regex(/^-.{1,5}|.{1,5}-|[1-5]$/gu)
  .brand<`PinyinSoundId`, `inout`>();
true satisfies IsEqual<PinyinSoundId, z.infer<typeof pinyinSoundIdSchema>>;

/**
 * An ID for a group of pinyin sounds.
 *
 * There is a default set of groups, and these groups have static IDs. In the
 * future user-defined groups may be added and these will have dynamic IDs.
 */
export type PinyinSoundGroupId = string & z.$brand<`PinyinSoundGroupId`>;
export const pinyinSoundGroupIdSchema = z.custom<PinyinSoundGroupId>(isString);

/**
 * A unique identifier for an asset stored in S3.
 *
 * Asset IDs are in the format: `sha256/<base64url-hash>`
 * where the hash is a 43-character base64url-encoded SHA-256 digest.
 */
export type AssetId = string & z.$brand<`AssetId`>;
export const assetIdSchema = z
  .string()
  .regex(/^sha256\/[A-Za-z0-9_-]{43}$/u, `Invalid AssetId format`)
  .brand<`AssetId`, `inout`>(); // `inout` makes it compatible with Inngest schemas

/**
 * A reusable actor record used by sound mnemonics.
 */
export type ActorId = string & z.$brand<`ActorId`>;
export const actorIdSchema = z.custom<ActorId>(isString);

export const openAiReasoningEffortSchema = z.enum([
  `none`,
  `minimal`,
  `low`,
  `medium`,
  `high`,
  `xhigh`,
  `max`,
]);

/**
 * A reusable place record used by pinyin finals mnemonic locations.
 */
export type LocationId = string & z.$brand<`LocationId`>;
export const locationIdSchema = z.custom<LocationId>(isString);

export const locationSetKeySchema = z.enum([
  `entrance`,
  `inside`,
  `basement`,
  `bathroom`,
  `backRoom`,
  `hiddenCloset`,
  `stairway`,
  /** @deprecated */
  `staircase`,
  /** @deprecated */
  `arrival`,
  /** @deprecated */
  `heart`,
  /** @deprecated */
  `below`,
  /** @deprecated */
  `ascent`,
  /** @deprecated */
  `summit`,
]);
export type LocationSetKey = z.infer<typeof locationSetKeySchema>;
export const locationSetKeys = locationSetKeySchema.options;

export const locationSetKindSchema = z.enum([]);
export type LocationSetKind = z.infer<typeof locationSetKindSchema>;

/**
 * Persisted location-set schema used for reading/writing user settings.
 *
 * Keep this permissive so older and newer saved payloads remain decodable while
 * prompt-generation schemas can evolve independently.
 */
export const locationSetSpecSchema = z
  .object({
    purpose: z.string().optional(),
  })
  .loose();

export type LocationSetSpec = z.infer<typeof locationSetSpecSchema>;

/**
 * Persisted location specification schema.
 *
 * Required fields are intentionally minimal to preserve backwards
 * compatibility with older stored location-spec versions.
 */
export const locationSpecSchema = z
  .object({
    location: z
      .string()
      .describe(
        `The name of the location using normal English capitalization as it would appear inside a sentence, not title capitalization. Proper nouns retain their normal capitalization; common nouns are lowercase.`,
      ),
    sets: z
      .partialRecord(locationSetKeySchema, locationSetSpecSchema)
      .optional(),
  })
  .loose();

export type LocationSpec = z.infer<typeof locationSpecSchema>;

export const pronunciationMnemonicSpecSchema = z
  .object({
    hook: z.string().optional(),
    premise: z.string().optional(),
    beats: z.array(z.string()).optional(),
    associationStrategy: z.string().optional(),
  })
  .loose();

export type PronunciationMnemonicSpec = z.infer<
  typeof pronunciationMnemonicSpecSchema
>;

/**
 * Persisted actor specification schema.
 *
 * Required fields are intentionally minimal to preserve backwards
 * compatibility with older stored actor-spec versions.
 */
export const actorSpecSchema = z
  .object({
    nickname: z.string(),
    /**
     * Specifying the species helps avoid the image models from generating a
     * non-human character when the actor is intended to be a human. Human race
     * can be included like `Human (Asian)` or `Human (Black)` if desired, but
     * is not required.
     */
    species: z.string().optional(),
    /**
     * Specifying the gender helps image model create the correct gender, and
     * the text models to generate the correct pronouns and gendered language.
     */
    gender: z.enum([`male`, `female`]).optional(),
  })
  .loose();

export type ActorSpec = z.infer<typeof actorSpecSchema>;

export const hanziWordPinyinlyObjectIdKind = `hw` as const;
export const skillPinyinlyObjectIdKind = `sk` as const;
export const pinyinSoundIdPinyinlyObjectIdKind = `ps` as const;
export const assetIdPinyinlyObjectIdKind = `a` as const;

/**
 * A polymorphic object ID that can reference different entity types in Pinyinly.
 *
 * Prefixes (aligned with Rizzle entity key paths):
 * - `hw/` → Hanzi word (e.g., `hw/好:positive`)
 * - `sk/` → Skill ID (e.g., `sk/he:好:positive`)
 * - `ps/` → Pinyin Sound ID (e.g., `ps/n-`)
 * - `a/` → Asset ID (e.g., `a/sha256/...`)
 */
export type PinyinlyObjectId =
  | `${typeof hanziWordPinyinlyObjectIdKind}/${string}`
  | `${typeof skillPinyinlyObjectIdKind}/${string}`
  | `${typeof pinyinSoundIdPinyinlyObjectIdKind}/${string}`
  | `${typeof assetIdPinyinlyObjectIdKind}/${string}`;

export const pinyinlyObjectIdKinds = [
  hanziWordPinyinlyObjectIdKind,
  skillPinyinlyObjectIdKind,
  pinyinSoundIdPinyinlyObjectIdKind,
  assetIdPinyinlyObjectIdKind,
] as const;

export type PinyinlyObjectIdKind = (typeof pinyinlyObjectIdKinds)[number];

export function pinyinlyObjectIdKind(
  objectId: PinyinlyObjectId,
): PinyinlyObjectIdKind | null {
  for (const kind of pinyinlyObjectIdKinds) {
    if (objectId.startsWith(`${kind}/`)) {
      return kind;
    }
  }
  return null;
}

export function hanziWordFromPinyinlyObjectId(
  objectId: PinyinlyObjectId,
): HanziWord | null {
  if (!objectId.startsWith(`${hanziWordPinyinlyObjectIdKind}/`)) {
    return null;
  }
  const hanziWord = objectId.slice(3);
  return hanziWord as HanziWord;
}

export function skillIdFromPinyinlyObjectId(
  objectId: PinyinlyObjectId,
): Skill | null {
  if (!objectId.startsWith(`${skillPinyinlyObjectIdKind}/`)) {
    return null;
  }
  const skillId = objectId.slice(3);
  return skillId as Skill;
}

export function soundIdFromPinyinlyObjectId(
  objectId: PinyinlyObjectId,
): PinyinSoundId | null {
  if (!objectId.startsWith(`${pinyinSoundIdPinyinlyObjectIdKind}/`)) {
    return null;
  }
  const soundId = objectId.slice(3);
  return soundId as PinyinSoundId;
}

export function assetIdFromPinyinlyObjectId(
  objectId: PinyinlyObjectId,
): AssetId | null {
  if (!objectId.startsWith(`${assetIdPinyinlyObjectIdKind}/`)) {
    return null;
  }
  const assetId = objectId.slice(2);
  return assetId as AssetId;
}

export function hanziWordPinyinlyObjectId(
  hanziWord: HanziWord,
): PinyinlyObjectId {
  return `${hanziWordPinyinlyObjectIdKind}/${hanziWord}`;
}

export function skillPinyinlyObjectId(skill: Skill): PinyinlyObjectId {
  return `${skillPinyinlyObjectIdKind}/${skill}`;
}

export function pinyinSoundIdPinyinlyObjectId(
  soundId: PinyinSoundId,
): PinyinlyObjectId {
  return `${pinyinSoundIdPinyinlyObjectIdKind}/${soundId}`;
}

export function assetIdPinyinlyObjectId(assetId: AssetId): PinyinlyObjectId {
  return `${assetIdPinyinlyObjectIdKind}/${assetId}`;
}

export interface BaseSrsState {
  prevReviewAt: Date;
  nextReviewAt: Date;
}

export const srsKindSchema = z.enum({
  Mock: `debug--Mock`,
  FsrsFourPointFive: `debug--FsrsFourPointFive`,
});
export const SrsKind = srsKindSchema.enum;
export type SrsKind = z.infer<typeof srsKindSchema>;

/**
 * Asset upload status for tracking optimistic uploads.
 *
 * - `pending`: Asset upload has been initiated but not confirmed
 * - `uploaded`: Asset has been successfully uploaded to storage
 * - `failed`: Asset upload failed
 */
export const assetStatusKindSchema = z.enum({
  Pending: `debug--Pending`,
  Uploaded: `debug--Uploaded`,
  Failed: `debug--Failed`,
});
export const AssetStatusKind = assetStatusKindSchema.enum;
export type AssetStatusKind = z.infer<typeof assetStatusKindSchema>;

/**
 * A placeholder to force the code to be structured to allow multiple SRS
 * algorithms. This is not used for anything.
 */
export interface SrsStateMockType extends BaseSrsState {
  kind: typeof SrsKind.Mock;
}

/**
 * FSRS 4.5 specific parameters.
 */
export interface SrsStateFsrsFourPointFiveType extends BaseSrsState {
  kind: typeof SrsKind.FsrsFourPointFive;
  stability: number;
  difficulty: number;
}

export type SrsStateType = SrsStateMockType | SrsStateFsrsFourPointFiveType;

const skillKindSchema = z.enum({
  /**
   * When shown a hanzi word, pick the english translation from a list (easy).
   */
  HanziWordToGloss: `debug--HanziWordToGloss`,
  /**
   * When shown a hanzi word, type the english translation (hard).
   */
  HanziWordToGlossTyped: `debug--HanziWordToGlossTyped`,
  /**
   * When shown a Hanzi be able to write the pinyin using the keyboard without
   * any other hints or multiple choice options.
   */
  HanziWordToPinyinTyped: `debug--HanziWordToPinyinTyped`,
  HanziWordToPinyinFinal: `debug--HanziWordToPinyinFinal`,
  HanziWordToPinyinInitial: `debug--HanziWordToPinyinInitial`,
  HanziWordToPinyinTone: `debug--HanziWordToPinyinTone`,
  /**
   * Given a character's meaning and pronunciation, write the character.
   */
  HanziCharacterToWriting: `debug--HanziCharacterToWriting`,
  /**
   * When shown a gloss, write the hanzi characters.
   */
  GlossToHanziWord: `debug--GlossToHanziWord`,
  /**
   * Given a pinyin word, write the hanzi character.
   */
  PinyinToHanziWord: `debug--PinyinToHanziWord`,
  ImageToHanziWord: `debug--ImageToHanziWord`,
  /**
   * Given an initial like `p`, remember the name of the associated
   * character/actor/entity etc.
   */
  PinyinInitialAssociation: `debug--PinyinInitialAssociation`,
  PinyinFinalAssociation: `debug--PinyinFinalAssociation`,

  //
  // Deprecated
  //
  Deprecated: `debug--Deprecated`,
  Deprecated_RadicalToEnglish: `debug--Deprecated_RadicalToEnglish`,
  Deprecated_EnglishToRadical: `debug--Deprecated_EnglishToRadical`,
  Deprecated_RadicalToPinyin: `debug--Deprecated_RadicalToPinyin`,
  Deprecated_PinyinToRadical: `debug--Deprecated_PinyinToRadical`,
});
export const SkillKind = skillKindSchema.enum;
export type SkillKind = z.infer<typeof skillKindSchema>;

/**
 * HSK 3.0 levels
 */
export const hsk30LevelSchema = z.enum({
  "1": `1`,
  "2": `2`,
  "3": `3`,
  "4": `4`,
  "5": `5`,
  "6": `6`,
  "7-9": `7-9`,
});
export const Hsk30Level = hsk30LevelSchema.enum;
export type Hsk30Level = z.infer<typeof hsk30LevelSchema>;

export const partOfSpeechSchema = z.enum({
  /** Names a person, place, thing, or idea; e.g. 学生 (student). */
  Noun: `debug--Noun (名)`,
  /** Stands in for a noun; e.g. 我 (I), 你 (you). */
  Pronoun: `debug--Pronoun (代)`,
  /** States a number; e.g. 三 (three), 十 (ten). */
  Numeral: `debug--Numeral (数)`,
  /** Counts or measures a noun; e.g. 个 in 三个人. */
  MeasureWord: `debug--MeasureWord (量)`,
  /** Expresses an amount or quantity; e.g. 几 (how many), 很多 (many). */
  Quantifier: `debug--Quantifier (数量)`,
  /** Expresses an action or state; e.g. 走 (walk), 喜欢 (like). */
  Verb: `debug--Verb (动)`,
  /** Helps another verb express modality or aspect; e.g. 会 (can/will). */
  Auxiliary: `debug--Auxiliary (助动)`,
  /** Describes a quality or state; e.g. 大 (big), 漂亮 (beautiful). */
  Adjective: `debug--Adjective (形)`,
  /** Modifies a verb, adjective, or sentence; e.g. 很 (very), 不 (not). */
  Adverb: `debug--Adverb (副)`,
  /** Relates a noun phrase to another phrase; e.g. 在 (at), 对 (toward). */
  Preposition: `debug--Preposition (介)`,
  /** Connects words or clauses; e.g. 和 (and), 但是 (but). */
  Conjunction: `debug--Conjunction (连)`,
  /** Adds grammatical or modal meaning; e.g. 的, 了, 吧. */
  Particle: `debug--Particle (助)`,
  /** Imitates a sound; e.g. 哈哈 (laughter), 轰 (boom). */
  Onomatopoeia: `debug--Onomatopoeia (拟声)`,
  /** Expresses a sudden reaction; e.g. 哎呀 (oh!), 喂 (hey!). */
  Interjection: `debug--Interjection (叹)`,
  /** Bound element placed before a base; e.g. 老 in 老王. */
  Prefix: `debug--Prefix (前缀)`,
  /** Bound element placed after a base; e.g. 子 in 桌子. */
  Suffix: `debug--Suffix (后缀)`,
  /** Fixed idiomatic expression; e.g. 一石二鸟 (kill two birds with one stone). */
  Idiom: `debug--Idiom (成语)`,
  /** Sentence element being talked about; e.g. 人 in 人来了. */
  Subject: `debug--Subject (主语)`,
  /** Says something about the subject; e.g. 来了 in 人来了. */
  Predicate: `debug--Predicate (谓语)`,
  /** Element acted on by a verb; e.g. 苹果 in 吃苹果. */
  Object: `debug--Object (宾语)`,
  /** Completes a verb or adjective's meaning; e.g. 完 in 吃完. */
  Complement: `debug--Complement (补语)`,
  /** Verb followed by its object; e.g. 看书 (read a book). */
  VerbObject: `debug--VerbObject (动宾结构)`,
  /** Verb followed by a result or degree; e.g. 看完 (finish reading). */
  VerbComplement: `debug--VerbComplement (动补结构)`,
  /** Phrase headed by a verb; e.g. 正在吃饭 (eating right now). */
  VerbalPhrase: `debug--VerbalPhrase (动词短语)`,
  /** Phrase headed by an adjective; e.g. 很漂亮 (very beautiful). */
  AdjectivePhrase: `debug--AdjectivePhrase (形容词短语)`,
});
export const PartOfSpeech = partOfSpeechSchema.enum;
export type PartOfSpeech = z.infer<typeof partOfSpeechSchema>;

export const partOfSpeechNameSchema = z.enum({
  Noun: `noun`,
  Pronoun: `pronoun`,
  Numeral: `numeral`,
  MeasureWord: `measure word`,
  Quantifier: `quantifier`,
  Verb: `verb`,
  Auxiliary: `auxiliary`,
  Adjective: `adjective`,
  Adverb: `adverb`,
  Preposition: `preposition`,
  Conjunction: `conjunction`,
  Particle: `particle`,
  Onomatopoeia: `onomatopoeia`,
  Interjection: `interjection`,
  Prefix: `prefix`,
  Suffix: `suffix`,
  Idiom: `idiom`,
  Subject: `subject`,
  Predicate: `predicate`,
  Object: `object`,
  Complement: `complement`,
  VerbObject: `verb-object`,
  VerbComplement: `verb-complement`,
  VerbalPhrase: `verbal phrase`,
  AdjectivePhrase: `adjective phrase`,
});
export const PartOfSpeechName = partOfSpeechNameSchema.enum;
export type PartOfSpeechName = z.infer<typeof partOfSpeechNameSchema>;

export const partOfSpeechAbbreviationSchema = z.enum({
  Noun: `n`,
  Pronoun: `pron`,
  Numeral: `num`,
  MeasureWord: `m`,
  Quantifier: `q`,
  Verb: `v`,
  Auxiliary: `aux`,
  Adjective: `adj`,
  Adverb: `adv`,
  Preposition: `prep`,
  Conjunction: `conj`,
  Particle: `part`,
  Onomatopoeia: `onom`,
  Interjection: `int`,
  Prefix: `pref`,
  Suffix: `suf`,
  Idiom: `idm`,
  Subject: `S`,
  Predicate: `P`,
  Object: `O`,
  Complement: `C`,
  VerbObject: `VO`,
  VerbComplement: `VC`,
  VerbalPhrase: `VP`,
  AdjectivePhrase: `AP`,
});
export const PartOfSpeechAbbr = partOfSpeechAbbreviationSchema.enum;
export type PartOfSpeechAbbr = z.infer<typeof partOfSpeechAbbreviationSchema>;

export const partOfSpeechAbbreviationByPartOfSpeech = {
  [PartOfSpeech.Noun]: PartOfSpeechAbbr.Noun,
  [PartOfSpeech.Pronoun]: PartOfSpeechAbbr.Pronoun,
  [PartOfSpeech.Numeral]: PartOfSpeechAbbr.Numeral,
  [PartOfSpeech.MeasureWord]: PartOfSpeechAbbr.MeasureWord,
  [PartOfSpeech.Quantifier]: PartOfSpeechAbbr.Quantifier,
  [PartOfSpeech.Verb]: PartOfSpeechAbbr.Verb,
  [PartOfSpeech.Auxiliary]: PartOfSpeechAbbr.Auxiliary,
  [PartOfSpeech.Adjective]: PartOfSpeechAbbr.Adjective,
  [PartOfSpeech.Adverb]: PartOfSpeechAbbr.Adverb,
  [PartOfSpeech.Preposition]: PartOfSpeechAbbr.Preposition,
  [PartOfSpeech.Conjunction]: PartOfSpeechAbbr.Conjunction,
  [PartOfSpeech.Particle]: PartOfSpeechAbbr.Particle,
  [PartOfSpeech.Onomatopoeia]: PartOfSpeechAbbr.Onomatopoeia,
  [PartOfSpeech.Interjection]: PartOfSpeechAbbr.Interjection,
  [PartOfSpeech.Prefix]: PartOfSpeechAbbr.Prefix,
  [PartOfSpeech.Suffix]: PartOfSpeechAbbr.Suffix,
  [PartOfSpeech.Idiom]: PartOfSpeechAbbr.Idiom,
  [PartOfSpeech.Subject]: PartOfSpeechAbbr.Subject,
  [PartOfSpeech.Predicate]: PartOfSpeechAbbr.Predicate,
  [PartOfSpeech.Object]: PartOfSpeechAbbr.Object,
  [PartOfSpeech.Complement]: PartOfSpeechAbbr.Complement,
  [PartOfSpeech.VerbObject]: PartOfSpeechAbbr.VerbObject,
  [PartOfSpeech.VerbComplement]: PartOfSpeechAbbr.VerbComplement,
  [PartOfSpeech.VerbalPhrase]: PartOfSpeechAbbr.VerbalPhrase,
  [PartOfSpeech.AdjectivePhrase]: PartOfSpeechAbbr.AdjectivePhrase,
} satisfies Record<PartOfSpeech, PartOfSpeechAbbr>;

export const partOfSpeechNameByPartOfSpeech = {
  [PartOfSpeech.Noun]: PartOfSpeechName.Noun,
  [PartOfSpeech.Pronoun]: PartOfSpeechName.Pronoun,
  [PartOfSpeech.Numeral]: PartOfSpeechName.Numeral,
  [PartOfSpeech.MeasureWord]: PartOfSpeechName.MeasureWord,
  [PartOfSpeech.Quantifier]: PartOfSpeechName.Quantifier,
  [PartOfSpeech.Verb]: PartOfSpeechName.Verb,
  [PartOfSpeech.Auxiliary]: PartOfSpeechName.Auxiliary,
  [PartOfSpeech.Adjective]: PartOfSpeechName.Adjective,
  [PartOfSpeech.Adverb]: PartOfSpeechName.Adverb,
  [PartOfSpeech.Preposition]: PartOfSpeechName.Preposition,
  [PartOfSpeech.Conjunction]: PartOfSpeechName.Conjunction,
  [PartOfSpeech.Particle]: PartOfSpeechName.Particle,
  [PartOfSpeech.Onomatopoeia]: PartOfSpeechName.Onomatopoeia,
  [PartOfSpeech.Interjection]: PartOfSpeechName.Interjection,
  [PartOfSpeech.Prefix]: PartOfSpeechName.Prefix,
  [PartOfSpeech.Suffix]: PartOfSpeechName.Suffix,
  [PartOfSpeech.Idiom]: PartOfSpeechName.Idiom,
  [PartOfSpeech.Subject]: PartOfSpeechName.Subject,
  [PartOfSpeech.Predicate]: PartOfSpeechName.Predicate,
  [PartOfSpeech.Object]: PartOfSpeechName.Object,
  [PartOfSpeech.Complement]: PartOfSpeechName.Complement,
  [PartOfSpeech.VerbObject]: PartOfSpeechName.VerbObject,
  [PartOfSpeech.VerbComplement]: PartOfSpeechName.VerbComplement,
  [PartOfSpeech.VerbalPhrase]: PartOfSpeechName.VerbalPhrase,
  [PartOfSpeech.AdjectivePhrase]: PartOfSpeechName.AdjectivePhrase,
} satisfies Record<PartOfSpeech, PartOfSpeechName>;

/**
 * A hanzi and meaning pair, e.g. `好:good`.
 *
 * Hanzi can have multiple meanings, so this offers a way to represent a word
 * with a specific meaning.
 */
export type HanziWord =
  | (string & z.$brand<`HanziWord`>)
  | `${string}:${string}`; // useful when writing literal strings in tests

/**
 * A branded type to represent a pinyin unit that's safe for use as an ID
 * because it's been normalized to a canonical form. This is used for keys in
 * the database and other places where a consistent representation of pinyin is
 * needed.
 *
 * The normalization is converting erhua to the canonical form, and converting
 * numeric tone notation to diacritic tone notation. For example, `hao3` would
 * be normalized to `hǎo`, and `er5` would be normalized to `ér`.
 *
 * This is only supported for PinyinUnit and not PinyinText, because PinyinText
 * can contain multiple units, spaces, punctuation, and anything else that could
 * be in a piece of text and it's not clear how to safely normalize that.
 */
export type PinyinUnitId = string & z.$brand<`PinyinUnitId`>;
export const pinyinUnitIdSchema = z.custom<PinyinUnitId>(isString);

/**
 * A single pinyin diacritic unit (e.g. `hǎo`). This should not include numeric
 * notation, use `normalizePinyin__` functions to convert numeric to diacritic
 * forms.
 *
 * A unit is a single sound or component (e.g. nǐ, or 儿 in 一点儿), so `nǐ hǎo`
 * would be two units: `nǐ` and `hǎo`. Erhua is considered two Pinyin units, so
 * `yìdiǎnr` would be three units: `yì`, `diǎn`, and `r`.
 */
export type PinyinUnit = PinyinUnitId | (string & z.$brand<`PinyinUnit`>);

export type PinyinText = PinyinUnit | (string & z.$brand<`PinyinText`>);

/**
 * A single pinyin unit in numeric tone form (e.g. `hao3`).
 *
 * A unit is a single sound or component (e.g. ni3, or r5 for 儿), so `ni3 hao3`
 * would be two units: `ni3` and `hao3`.
 */
export type PinyinNumericUnit = string & z.$brand<`PinyinNumericUnit`>;
export type PinyinNumericText =
  | PinyinNumericUnit
  | (string & z.$brand<`PinyinNumericText`>);

/**
 * Space-separated pinyin units, used for efficient storage.
 *
 * Being space-separated (rather than no-separation) makes it simpler to split
 * back into individual pinyin units rather than parsing valid pinyin
 * unit boundaries.
 */
export type PinyinPronunciationSpaceSeparated = string &
  z.$brand<`PinyinPronunciationSpaceSeparated`>;

/**
 * Single Hanzi character (in the Unicode sense).
 *
 * This is the hanzi companion to {@link PinyinUnit}.
 */
export type HanziCharacter = string & z.$brand<`HanziCharacter`>;

/**
 * Non-space separated hanzi text.
 */
export type HanziText = (string & z.$brand<`HanziText`>) | HanziCharacter;

export const hanziTextSchema = z.custom<HanziText>(isString);
export const pylyMarkSchema = z.string();
export const hanziWordSchema = z.custom<HanziWord>(isString);
export const hanziCharacterSchema = z.custom<HanziCharacter>(isString);
export const pinyinTextSchema = z.string() as unknown as z.ZodCustom<
  PinyinText,
  PinyinText
>;
export const pinyinUnitSchema = z.custom<PinyinUnit>(isString);

export type HanziWordSkillKind =
  | typeof SkillKind.HanziWordToGloss
  | typeof SkillKind.HanziWordToGlossTyped
  | typeof SkillKind.HanziWordToPinyinTyped
  | typeof SkillKind.HanziWordToPinyinInitial
  | typeof SkillKind.HanziWordToPinyinFinal
  | typeof SkillKind.HanziWordToPinyinTone
  | typeof SkillKind.GlossToHanziWord
  | typeof SkillKind.PinyinToHanziWord
  | typeof SkillKind.ImageToHanziWord;

export const hanziWordSkillKinds: readonly HanziWordSkillKind[] = [
  SkillKind.HanziWordToGloss,
  SkillKind.HanziWordToGlossTyped,
  SkillKind.HanziWordToPinyinInitial,
  SkillKind.HanziWordToPinyinFinal,
  SkillKind.HanziWordToPinyinTone,
  SkillKind.HanziWordToPinyinTyped,
  SkillKind.GlossToHanziWord,
];

const questionFlagKindSchema = z.enum({
  Blocked: `debug--Blocked`,
  NewDifficulty: `debug--NewDifficulty`,
  NewSkill: `debug--NewSkill`,
  OtherAnswer: `debug--OtherAnswer`,
  Overdue: `debug--Overdue`,
  Retry: `debug--Retry`,
  WeakWord: `debug--WeakWord`,
});
export const QuestionFlagKind = questionFlagKindSchema.enum;
export type QuestionFlagKind = z.infer<typeof questionFlagKindSchema>;

export interface QuestionFlagBlockedType {
  kind: typeof QuestionFlagKind.Blocked;
}

export interface QuestionFlagRetryType {
  kind: typeof QuestionFlagKind.Retry;
}

export interface QuestionFlagOverdueType {
  kind: typeof QuestionFlagKind.Overdue;
  interval: Interval;
}

export interface QuestionFlagOtherAnswerType {
  kind: typeof QuestionFlagKind.OtherAnswer;
  /**
   * When there are multiple meanings for a hanzi word, the previously given
   * meanings should be avoided when answering the question again.
   */
  previousHanziWords?: readonly HanziWord[];
}

export interface QuestionFlagNewDifficultyType {
  kind: typeof QuestionFlagKind.NewDifficulty;
}

export interface QuestionFlagNewSkillType {
  kind: typeof QuestionFlagKind.NewSkill;
}

export interface QuestionFlagWeakWordType {
  kind: typeof QuestionFlagKind.WeakWord;
}

export type QuestionFlagType =
  | QuestionFlagBlockedType
  | QuestionFlagNewDifficultyType
  | QuestionFlagNewSkillType
  | QuestionFlagOtherAnswerType
  | QuestionFlagOverdueType
  | QuestionFlagRetryType
  | QuestionFlagWeakWordType;

const questionKindSchema = z.enum({
  OneCorrectPair: `debug--OneCorrectPair`,
  HanziWordToPinyinTyped: `debug--HanziWordToPinyinTyped`,
  HanziWordToGlossTyped: `debug--HanziWordToGlossTyped`,
  HanziCharacterWriting: `debug--HanziCharacterWriting`,
});
export const QuestionKind = questionKindSchema.enum;
export type QuestionKind = z.infer<typeof questionKindSchema>;

const mistakeKindSchema = z.enum({
  /**
   * Mistakenly matching a hanzi with the wrong gloss.
   */
  HanziGloss: `debug--HanziGloss`,
  /**
   * Mistakenly matching a hanzi with the wrong pinyin. This can be used for
   * both HanziWord and plain Hanzi mistakes.
   */
  HanziPinyin: `debug--HanziPinyin`,
  /**
   * Mistakenly matching a hanzi with the wrong pinyin initial.
   *
   * This **is not specific** to a particular HanziWord.
   */
  HanziPinyinInitial: `debug--HanziPinyinInitial`,
});
export const MistakeKind = mistakeKindSchema.enum;
export type MistakeKind = z.infer<typeof mistakeKindSchema>;

export interface HanziGlossMistakeType {
  kind: typeof MistakeKind.HanziGloss;
  /**
   * This can be either a HanziWord or a plain Hanzi character.
   *
   * It should be a HanziWord when the user was shown a specific HanziWord and
   * they answered with the wrong pinyin. The Pinyin might have been correct for
   * another meaning of the same hanzi, but it was incorrect for the meaning
   * they were shown.
   */
  hanziOrHanziWord: HanziWord | HanziText;
  gloss: string;
}

export interface HanziPinyinMistakeType {
  kind: typeof MistakeKind.HanziPinyin;
  /**
   * This can be either a HanziWord or a plain Hanzi character,
   * {@link HanziGlossMistakeType} for a rationale.
   */
  hanziOrHanziWord: HanziWord | HanziText;
  /**
   * This is intentionally **not** a {@link PinyinText} and instead a
   * string because the user might have answered with junk and there's no
   * guarantee what it is.
   */
  pinyin: string;
}

export interface HanziPinyinInitialMistakeType {
  kind: typeof MistakeKind.HanziPinyinInitial;
  hanzi: string;
  pinyinInitial: string;
}

export type MistakeType =
  | HanziGlossMistakeType
  | HanziPinyinMistakeType
  | HanziPinyinInitialMistakeType;

export interface UnsavedSkillRating {
  skill: Skill;
  rating: Rating;
  durationMs: number;
}

export type OneCorrectPairQuestionHanziChoice = {
  kind: `hanzi`;
  value: HanziText;
};

export type OneCorrectPairQuestionGlossChoice = {
  kind: `gloss`;
  value: string;
};

export type OneCorrectPairQuestionPinyinChoice = {
  kind: `pinyin`;
  value: PinyinText;
};

export type OneCorrectPairQuestionChoice =
  | OneCorrectPairQuestionHanziChoice
  | OneCorrectPairQuestionGlossChoice
  | OneCorrectPairQuestionPinyinChoice;

export interface OneCorrectPairQuestionAnswer {
  as: readonly OneCorrectPairQuestionChoice[];
  bs: readonly OneCorrectPairQuestionChoice[];
  skill: Skill;
}

export interface OneCorrectPairQuestion {
  kind: typeof QuestionKind.OneCorrectPair;
  prompt: string;
  answer: OneCorrectPairQuestionAnswer;
  groupA: readonly OneCorrectPairQuestionChoice[];
  groupB: readonly OneCorrectPairQuestionChoice[];
  flag: QuestionFlagType | null;
}

export interface HanziWordToGlossTypedQuestion {
  kind: typeof QuestionKind.HanziWordToGlossTyped;
  /**
   * There can be multiple correct answers, e.g. for a hanzi word like `好:good`
   * has multiple correct glosses i.e. "good", "nice", "friendly".
   */
  answers: { skill: HanziWordSkill; glosses: string[] }[];
  /**
   * Previous glosses that should be avoided.
   */
  bannedMeaningPrimaryGlossHint: readonly string[];
  /**
   * The skill being quizzed, used for rating a wrong answer.
   */
  skill: HanziWordSkill;
  flag: QuestionFlagType | null;
}

export interface HanziWordToPinyinTypedQuestion {
  kind: typeof QuestionKind.HanziWordToPinyinTyped;
  /**
   * There can be multiple correct answers, e.g. for a hanzi that has multiple
   * meanings with different pronunciations.
   */
  answers: {
    skill: HanziWordSkill;
    pinyin: readonly PinyinText[];
  }[];
  /**
   * Previous pinyin that should be avoided.
   */
  bannedMeaningPinyinHint: readonly PinyinText[];
  /**
   * The skill being quizzed, used for rating a wrong answer.
   */
  skill: HanziWordSkill;
  flag: QuestionFlagType | null;
}

export interface HanziCharacterWritingQuestion {
  kind: typeof QuestionKind.HanziCharacterWriting;
  skill: HanziCharacterWritingSkill;
  hanzi: HanziCharacter;
  gloss: string;
  pinyin: PinyinText;
  flag: QuestionFlagType | null;
}

export type Question =
  | OneCorrectPairQuestion
  | HanziWordToGlossTypedQuestion
  | HanziWordToPinyinTypedQuestion
  | HanziCharacterWritingQuestion;

export interface PinyinInitialAssociation {
  initial: string;
  name: string;
}

export interface PinyinFinalAssociation {
  final: string;
  name: string;
}

export type StrokeSpecString = string & z.$brand<`StrokeSpecString`>;
export const strokeSpecStringSchema = z
  .string()
  .regex(/^[\d#[\]:+,%.-]*$/gu)
  .brand<`StrokeSpecString`, `inout`>();
true satisfies IsEqual<
  StrokeSpecString,
  z.infer<typeof strokeSpecStringSchema>
>;

/**
 * A Hanzi IDS string.
 */
export type HanziIds = string & z.$brand<`HanziIds`>;
export const hanziIdsSchema = z
  .string()
  .regex(
    // 1. combining characters
    // 2. enclosed alphanumerics
    // 3. Han script
    // 4. CJK strokes
    /^(?:[⿰⿱⿲⿳⿴⿵⿶⿷⿼⿸⿹⿺⿽⿻⿾⿿]|[\u2460-\u2473]|\p{Script=Han}|[\u31C0-\u31EFコュス])+$/gu,
  )
  .brand<`HanziIds`, `inout`>();
true satisfies IsEqual<HanziIds, z.infer<typeof hanziIdsSchema>>;

export const idsOperatorSchema = z.enum({
  LeftToRight: `⿰`,
  AboveToBelow: `⿱`,
  LeftToMiddleToRight: `⿲`,
  AboveToMiddleAndBelow: `⿳`,
  FullSurround: `⿴`,
  SurroundFromAbove: `⿵`,
  SurroundFromBelow: `⿶`,
  SurroundFromLeft: `⿷`,
  SurroundFromRight: `⿼`,
  SurroundFromUpperLeft: `⿸`,
  SurroundFromUpperRight: `⿹`,
  SurroundFromLowerLeft: `⿺`,
  SurroundFromLowerRight: `⿽`,
  Overlaid: `⿻`,
  HorizontalReflection: `⿾`,
  Rotation: `⿿`,
});

const IdsOperator = idsOperatorSchema.enum;
type IdsOperator = z.infer<typeof idsOperatorSchema>;

export { IdsOperator };

const idsOperatorArity1 = z.union([
  z.literal(IdsOperator.HorizontalReflection),
  z.literal(IdsOperator.Rotation),
]);

const idsOperatorArity2 = z.union([
  z.literal(IdsOperator.LeftToRight),
  z.literal(IdsOperator.AboveToBelow),
  z.literal(IdsOperator.FullSurround),
  z.literal(IdsOperator.SurroundFromAbove),
  z.literal(IdsOperator.SurroundFromBelow),
  z.literal(IdsOperator.SurroundFromLeft),
  z.literal(IdsOperator.SurroundFromRight),
  z.literal(IdsOperator.SurroundFromUpperLeft),
  z.literal(IdsOperator.SurroundFromUpperRight),
  z.literal(IdsOperator.SurroundFromLowerLeft),
  z.literal(IdsOperator.SurroundFromLowerRight),
  z.literal(IdsOperator.Overlaid),
]);

const idsOperatorArity3 = z.union([
  z.literal(IdsOperator.LeftToMiddleToRight),
  z.literal(IdsOperator.AboveToMiddleAndBelow),
]);

export type IdsOperatorArity1 = z.infer<typeof idsOperatorArity1>;
export type IdsOperatorArity2 = z.infer<typeof idsOperatorArity2>;
export type IdsOperatorArity3 = z.infer<typeof idsOperatorArity3>;

export type IdsNode<T> =
  | [typeof IdsOperator.LeftToRight, IdsNode<T>, IdsNode<T>]
  | [typeof IdsOperator.AboveToBelow, IdsNode<T>, IdsNode<T>]
  | [typeof IdsOperator.LeftToMiddleToRight, IdsNode<T>, IdsNode<T>, IdsNode<T>]
  | [
      typeof IdsOperator.AboveToMiddleAndBelow,
      IdsNode<T>,
      IdsNode<T>,
      IdsNode<T>,
    ]
  | [typeof IdsOperator.FullSurround, IdsNode<T>, IdsNode<T>]
  | [typeof IdsOperator.SurroundFromAbove, IdsNode<T>, IdsNode<T>]
  | [typeof IdsOperator.SurroundFromBelow, IdsNode<T>, IdsNode<T>]
  | [typeof IdsOperator.SurroundFromLeft, IdsNode<T>, IdsNode<T>]
  | [typeof IdsOperator.SurroundFromRight, IdsNode<T>, IdsNode<T>]
  | [typeof IdsOperator.SurroundFromUpperLeft, IdsNode<T>, IdsNode<T>]
  | [typeof IdsOperator.SurroundFromUpperRight, IdsNode<T>, IdsNode<T>]
  | [typeof IdsOperator.SurroundFromLowerLeft, IdsNode<T>, IdsNode<T>]
  | [typeof IdsOperator.SurroundFromLowerRight, IdsNode<T>, IdsNode<T>]
  | [typeof IdsOperator.Overlaid, IdsNode<T>, IdsNode<T>]
  | [typeof IdsOperator.HorizontalReflection, IdsNode<T>]
  | [typeof IdsOperator.Rotation, IdsNode<T>]
  | T;

export function buildIdsNodeSchema<T extends z.ZodType>(
  leafSchema: T,
): z.ZodType<IdsNode<z.infer<T>>> {
  const depth0Schema = leafSchema;

  const depth1Schema = z.union([
    depth0Schema,
    z.tuple([idsOperatorArity1, depth0Schema]),
    z.tuple([idsOperatorArity2, depth0Schema, depth0Schema]),
    z.tuple([idsOperatorArity3, depth0Schema, depth0Schema, depth0Schema]),
  ]);

  const depth2Schema = z.union([
    depth1Schema,
    z.tuple([idsOperatorArity1, depth1Schema]),
    z.tuple([idsOperatorArity2, depth1Schema, depth1Schema]),
    z.tuple([idsOperatorArity3, depth1Schema, depth1Schema, depth1Schema]),
  ]);

  const depth3Schema = z.union([
    depth2Schema,
    z.tuple([idsOperatorArity1, depth2Schema]),
    z.tuple([idsOperatorArity2, depth2Schema, depth2Schema]),
    z.tuple([idsOperatorArity3, depth2Schema, depth2Schema, depth2Schema]),
  ]);

  const depth4Schema = z.union([
    depth3Schema,
    z.tuple([idsOperatorArity1, depth3Schema]),
    z.tuple([idsOperatorArity2, depth3Schema, depth3Schema]),
    z.tuple([idsOperatorArity3, depth3Schema, depth3Schema, depth3Schema]),
  ]);

  const depth5Schema = z.union([
    depth4Schema,
    z.tuple([idsOperatorArity1, depth4Schema]),
    z.tuple([idsOperatorArity2, depth4Schema, depth4Schema]),
    z.tuple([idsOperatorArity3, depth4Schema, depth4Schema, depth4Schema]),
  ]);

  return depth5Schema as z.ZodType<IdsNode<z.infer<T>>>;
}

export const characterCurriculumMeaningBranchSchema = z.object({
  gloss: z.string(),
  description: z.string().optional(),
  occurrences: z
    .record(hanziTextSchema, pinyinTextSchema)
    .describe(
      `Occurrences of this meaning, keyed by hanzi with their pinyin. e.g. { "你好": "nǐhǎo" }`,
    ),
});

export const characterExampleSentenceSchema = z.strictObject({
  hanzi: hanziTextSchema,
  pinyin: pinyinTextSchema,
  translation: z.string(),
});

export type CharacterExampleSentence = z.infer<
  typeof characterExampleSentenceSchema
>;

export const characterCurriculumMeaningExampleSchema = z.strictObject({
  id: hanziWordSchema,
  exampleSentence: characterExampleSentenceSchema,
});

export type CharacterCurriculumMeaningExample = z.infer<
  typeof characterCurriculumMeaningExampleSchema
>;

/**
 * Curriculum Meanings are the semantic units used by Pinyinly's teaching
 * curriculum. They are deliberately different from dictionary senses.
 *
 * A dictionary describes how a character is actually used in the language,
 * often splitting it into many fine-grained senses. A Curriculum Meaning
 * instead groups one or more related dictionary senses into a single,
 * memorable concept that is easier for learners to understand and remember.
 *
 * Every Curriculum Meaning is associated with a single pronunciation and acts
 * as a conceptual "anchor" from which more specific branches and vocabulary
 * naturally grow. The goal is pedagogical clarity rather than lexicographic
 * precision.
 *
 * This distinction allows the curriculum to remain stable even if the
 * underlying dictionary changes. Dictionary senses can be merged, split, or
 * remapped over time without changing the conceptual structure presented to
 * learners.
 *
 * Information architecture:
 *
 *   Character
 *     ├─ Dictionary senses      (reference data)
 *     ├─ Curriculum meanings    (teaching model)
 *     │    ├─ branches
 *     │    └─ occurrences
 *     └─ Mnemonics
 *
 * Dictionary data answers "What meanings does this character have?".
 * Curriculum Meanings answer "What is the best way to teach those meanings?".
 */
export const characterCurriculumMeaningSchema = z.object({
  id: hanziWordSchema,
  gloss: z.string(),
  pinyin: pinyinUnitSchema,
  partOfSpeech: partOfSpeechAbbreviationSchema.optional(),
  hsk30: hsk30LevelSchema.optional(),
  pinyinExceptions: z
    .record(hanziTextSchema, pinyinTextSchema)
    .optional()
    .describe(
      `Exceptions to the default pinyin for this meaning, keyed by hanzi, e.g. { "你好": "nǐhǎo" }`,
    ),
  exampleSentence: characterExampleSentenceSchema.optional(),
  description: z.string().optional(),
  branches: z.array(characterCurriculumMeaningBranchSchema).optional(),
});

export const hanziStrokeColorSchema = z.enum([
  `blue`,
  `yellow`,
  `amber`,
  `cyanold`,
  `fg`,
]);

export type HanziStrokeColor = z.infer<typeof hanziStrokeColorSchema>;

export function hanziStrokeCountAsNumber(str: string): number | undefined {
  if (str.length === 1) {
    const codePoint = str.codePointAt(0);
    if (
      codePoint != null &&
      codePoint >= /* ① */ 9312 &&
      codePoint <= /* ⑳ */ 9331
    ) {
      return codePoint - 9311;
    }
  }
  return undefined;
}

export function isHanziStrokeCountChar(
  str: string,
): str is HanziStrokeCountChar {
  return hanziStrokeCountAsNumber(str) != null;
}

/**
 * A single hanzi character that represents a stroke count (①, ②, ③, …, ⑳).
 *
 * This is used in IDS strings to represent the number of strokes in a component
 * when the component itself does not have a single-character representation.
 */
export const hanziStrokeCountCharSchema = z
  .string()
  .refine((str) => hanziStrokeCountAsNumber(str) != null)
  .brand<`HanziStrokeCountChar`, `inout`>();

export type HanziStrokeCountChar = z.infer<typeof hanziStrokeCountCharSchema>;

export type HanziIdsLeaf = HanziCharacter | HanziStrokeCountChar;

export interface CharacterDecompositionRow {
  hanzi: HanziCharacter;
  ids: HanziIds;
  strokeSpecs: readonly StrokeSpecString[];
}

export interface CharacterComponentUsageRow {
  component: HanziCharacter;
  usedInHanzi: readonly HanziCharacter[];
}

export interface CharacterMnemonicIdsRow {
  hanzi: HanziCharacter;
  ids: HanziIds;
}

export interface CharacterCollectionRow {
  hanzi: HanziCharacter;
  isStructural: boolean;
  isComponent: boolean;
  strokes: number;
  componentFormOf: HanziCharacter | null;
}

export interface MnemonicHanziComponent {
  /**
   * Could be `null` if there's no unicode character to represent this component
   * (might be a radical or other component that doesn't have a single
   * character).
   */
  hanzi: HanziCharacter | null;
  strokeSpec?: StrokeSpecString | null;
  label?: string | null;
  color?: HanziStrokeColor | null;
}

export const characterJsonSvgSchema = z.strictObject({
  /**
   * Stroke outlines as SVG paths, or just the stroke count.
   */
  strokes: z.union([
    z.number().describe(`Stroke count`),
    z
      .array(z.string())
      .describe(`SVG outline paths for each stroke (in order)`),
  ]),
  /**
   * SVG path data for each stroke's median centerline, stored in stroke order.
   */
  medians: z
    .array(z.string())
    .optional()
    .describe(
      `Stroke median centerlines as SVG path strings, in stroke order.`,
    ),
  /**
   * Precomputed SVG paths for slice atoms keyed by canonical StrokeSpec atom text.
   *
   * Example key: "4[1:2]"
   */
  segments: z
    .record(z.string(), z.string())
    .optional()
    .describe(
      `Precomputed SVG segment paths keyed by canonical StrokeSpec slice atom.`,
    ),
});

export type CharacterJsonSvg = z.infer<typeof characterJsonSvgSchema>;

/**
 * Schema for character.json files.
 */
export const characterJsonSchema = z.strictObject({
  /**
   * The hanzi character represented by this character (e.g. 看).
   */
  hanzi: hanziCharacterSchema,
  /**
   * Characters with similar visual shapes, used for contrastive writing grading.
   */
  visuallySimilar: z.array(hanziCharacterSchema).optional(),
  /**
   * SVG-related data (strokes, medians, and precomputed segment paths).
   */
  svg: characterJsonSvgSchema,
  /**
   * The simplified form of this character, if it is a traditional form.
   *
   * The property is used on traditional characters because it's expected there
   * are fewer of those in the dataset since this app focuses on Mandarin.
   */
  simplifiedForm: hanziCharacterSchema.optional(),
  /**
   * If this character is a component form of another character, that hanzi.
   */
  componentFormOf: hanziCharacterSchema.optional(),
  /**
   * If this is variant of another character (for the purposes of learning),
   * point to the canonical form.
   *
   * e.g. ⺁ -> 厂
   */
  canonicalForm: hanziCharacterSchema.optional(),
  isStructural: z
    .literal(true)
    .optional()
    .describe(
      `is used as a component in regular Hanzi characters (e.g. parts of 兰, 兴, etc.), but never used independently as a full word or character in modern Mandarin.`,
    ),
  /**
   * Alternative IDS decompositions
   *
   * TODO: rename to `decompositions` grep the code base for other instances too
   */
  decompositions: z
    .record(
      hanziIdsSchema.describe(`partial or full IDS decomposition`),
      z
        .array(strokeSpecStringSchema)
        .describe(`strokeSpec for each leaf hanzi, in DFS order`),
    )
    .optional(),
  /**
   * The meaning mnemonic for the character. This doesn't necessarily correspond
   * to the etymological components, and their meanings can differ too. It's
   * intended for beginner learners and optimised for mnemonic usefulness.
   */
  mnemonic: z
    .strictObject({
      /**
       * Can be `null` if none of the decompositions should be used for the
       * mnemonic (to avoid unnecessarily learning a component that is not
       * relevant to the mnemonic).
       */
      decomposition: hanziIdsSchema
        .describe(`reference an IDS key in decompositions`)
        .nullable()
        .optional(),
      /**
       * Override annotations for the components. Keys are component hanzi, values are the modifiers.
       */
      components: z
        .record(
          hanziCharacterSchema,
          z
            .object({
              /**
               * Override the normal gloss for the component.
               */
              label: z.string().optional(),
              /**
               * What color to render this component in the decomposition illustration. This
               * allows highlighting different components in different colors for clarity.
               */
              color: z.string().optional(),
            })
            .strict(),
        )
        .optional(),
      hints: z
        .array(
          z.strictObject({
            meaningKey: z.string(),
            hint: z.string(),
            explanation: z.string().optional(),
            imageAssetIds: z.array(z.string()).optional(),
          }),
        )
        .optional(),
    })
    .optional(),
  /**
   * The meanings that are taught for this character in the curriculum.
   */
  curriculumMeanings: z.array(characterCurriculumMeaningSchema).optional(),
});

export type CharacterJson = z.infer<typeof characterJsonSchema>;

export const charactersJsonSchema = z.array(
  z.tuple([
    hanziCharacterSchema,
    z.object({
      mnemonic: hanziIdsSchema
        .describe(`IDS used by the mnemonic, affects learning order`)
        .nullable()
        .optional(),
      decompositions: z
        .record(
          hanziIdsSchema.describe(`IDS (can include stroke count characters)`),
          z.array(strokeSpecStringSchema),
        )
        .describe(`all IDS decompositions`)
        .optional(),
      componentFormOf: hanziCharacterSchema
        .describe(
          `the primary form of this hanzi (only relevant for component-form hanzi)`,
        )
        .optional(),
      isStructural: z
        .literal(true)
        .optional()
        .describe(
          `is used as a component in regular Hanzi characters (e.g. parts of 兰, 兴, etc.), but never used independently as a full word or character in modern Mandarin.`,
        ),
      strokes: z.number().describe(`the number of strokes in this hanzi`),
      canonicalForm: hanziCharacterSchema.optional(),
    }),
  ]),
);

export type CharactersJsonKey = z.infer<typeof charactersJsonSchema.element>[0];
export type CharactersJsonValue = z.infer<
  typeof charactersJsonSchema.element
>[1];

/**
 * Allowed image MIME types for uploads and AI generation.
 */
export const allowedImageMimeTypeEnum = z.enum([
  `image/jpeg`,
  `image/png`,
  `image/webp`,
  `image/gif`,
]);

export type AllowedImageMimeType = z.infer<typeof allowedImageMimeTypeEnum>;

/**
 * A reference image to be included in AI generation requests.
 * Contains a descriptive label, base64-encoded image data, and MIME type.
 */
export interface AiReferenceImage {
  label?: string;
  data: string; // Base64-encoded image data
  mimeType: AllowedImageMimeType;
}

/**
 * The type of the dictionary index returned by {@link loadDictionary}.
 */
export interface Dictionary {
  lookupHanzi(hanzi: HanziText): readonly HanziWordWithMeaning[];
  lookupHanziWord(hanzi: HanziWord): DeepReadonly<HanziWordMeaning> | null;
  lookupGloss(gloss: string): readonly HanziWordWithMeaning[];
  lookupPinyinUnit(pinyinUnit: PinyinUnit): readonly HanziCharacter[];
  isStructuralHanzi(hanzi: HanziCharacter): boolean;
  allEntries: readonly [HanziWord, DeepReadonly<HanziWordMeaning>][];
  allHanziWords: readonly HanziWord[];
  hsk1HanziWords: readonly HanziWord[];
  hsk2HanziWords: readonly HanziWord[];
  hsk3HanziWords: readonly HanziWord[];
  hsk4HanziWords: readonly HanziWord[];
  hsk5HanziWords: readonly HanziWord[];
  hsk6HanziWords: readonly HanziWord[];
  hsk7To9HanziWords: readonly HanziWord[];
}

const parsePosPattern = new RegExp(
  `^(?:` +
    [
      `(?<noun>noun|名|n)`,
      `(?<verb>verb|动|v)`,
      `(?<adjective>adjective|形|adj|vs)`,
      `(?<adverb>adverb|副|adv)`,
      `(?<pronoun>pronoun|代|pron|det)`,
      `(?<numeral>numeral|数词|数|num)`,
      `(?<measureWord>measure word|measureWord|classifier|量词|量|m|mw)`,
      `(?<quantifier>quantifier|数量词|数量|q)`,
      `(?<auxiliary>auxiliary|助动词|助动|aux)`,
      `(?<preposition>preposition|介|prep)`,
      `(?<conjunction>conjunction|连|conj)`,
      `(?<particle>particle|助词|助|part|ptc)`,
      `(?<onomatopoeia>onomatopoeia|phonetic|拟声词|拟声|onom|pho)`,
      `(?<interjection>interjection|叹|int)`,
      `(?<prefix>prefix|前缀|pref|pre)`,
      `(?<suffix>suffix|后缀|suf)`,
      `(?<idiom>idiom|成语|idm)`,
      `(?<subject>subject|主语|s)`,
      `(?<predicate>predicate|谓语|p)`,
      `(?<object>object|宾语|o)`,
      `(?<complement>complement|补语|c)`,
      `(?<verbObject>verb-object|动宾结构|vo)`,
      `(?<verbComplement>verb-complement|动补结构|vc)`,
      `(?<verbalPhrase>verbal phrase|动词短语|vp)`,
      `(?<adjectivePhrase>adjective phrase|形容词短语|ap)`,
    ].join(`|`) +
    `)$`,
  `iu`,
);

export function parsePartOfSpeech(pos: string): PartOfSpeech | undefined {
  const match = parsePosPattern.exec(pos);
  if (match?.groups?.[`noun`] != null) {
    return PartOfSpeech.Noun;
  } else if (match?.groups?.[`verb`] != null) {
    return PartOfSpeech.Verb;
  } else if (match?.groups?.[`adjective`] != null) {
    return PartOfSpeech.Adjective;
  } else if (match?.groups?.[`adverb`] != null) {
    return PartOfSpeech.Adverb;
  } else if (match?.groups?.[`pronoun`] != null) {
    return PartOfSpeech.Pronoun;
  } else if (match?.groups?.[`numeral`] != null) {
    return PartOfSpeech.Numeral;
  } else if (match?.groups?.[`measureWord`] != null) {
    return PartOfSpeech.MeasureWord;
  } else if (match?.groups?.[`quantifier`] != null) {
    return PartOfSpeech.Quantifier;
  } else if (match?.groups?.[`auxiliary`] != null) {
    return PartOfSpeech.Auxiliary;
  } else if (match?.groups?.[`preposition`] != null) {
    return PartOfSpeech.Preposition;
  } else if (match?.groups?.[`conjunction`] != null) {
    return PartOfSpeech.Conjunction;
  } else if (match?.groups?.[`particle`] != null) {
    return PartOfSpeech.Particle;
  } else if (match?.groups?.[`onomatopoeia`] != null) {
    return PartOfSpeech.Onomatopoeia;
  } else if (match?.groups?.[`interjection`] != null) {
    return PartOfSpeech.Interjection;
  } else if (match?.groups?.[`prefix`] != null) {
    return PartOfSpeech.Prefix;
  } else if (match?.groups?.[`suffix`] != null) {
    return PartOfSpeech.Suffix;
  } else if (match?.groups?.[`idiom`] != null) {
    return PartOfSpeech.Idiom;
  } else if (match?.groups?.[`subject`] != null) {
    return PartOfSpeech.Subject;
  } else if (match?.groups?.[`predicate`] != null) {
    return PartOfSpeech.Predicate;
  } else if (match?.groups?.[`object`] != null) {
    return PartOfSpeech.Object;
  } else if (match?.groups?.[`complement`] != null) {
    return PartOfSpeech.Complement;
  } else if (match?.groups?.[`verbObject`] != null) {
    return PartOfSpeech.VerbObject;
  } else if (match?.groups?.[`verbComplement`] != null) {
    return PartOfSpeech.VerbComplement;
  } else if (match?.groups?.[`verbalPhrase`] != null) {
    return PartOfSpeech.VerbalPhrase;
  } else if (match?.groups?.[`adjectivePhrase`] != null) {
    return PartOfSpeech.AdjectivePhrase;
  }
  return undefined;
}

const cedictCompactReferenceRe = /^\S+\s+\S+\s+\[\[.*?\]\]\s+[A-Za-z0-9]{5}$/u;

export const cedictReferenceSchema = z
  .string()
  .regex(cedictCompactReferenceRe, {
    message: `CE-DICT reference must follow this format: traditional simplified [[pinyin]] NANOID`,
  });

export type CedictReference = z.infer<typeof cedictReferenceSchema>;

export const hanziWordMeaningSchema = z
  .object({
    gloss: z.array(z.string()),
    order: z
      .number()
      .optional()
      .describe(`order of this meaning in the dictionary entry, first is 0`),
    freq: z
      .number()
      .min(0)
      .max(1)
      .describe(
        `normalized meaning frequency where higher means more common usage`,
      )
      .optional(),
    pinyin: z
      .array(pinyinTextSchema)
      .describe(
        `all valid pinyin variations for this meaning (might be omitted for radicals without pronunciation)`,
      )
      .nullable()
      .optional(),
    pos: z
      .string()
      .transform((x) => parsePartOfSpeech(x))
      .optional(),
    hsk: hsk30LevelSchema.optional(),
    cedict: cedictReferenceSchema
      .describe(`reference to the corresponding CE-DICT entry and sense`)
      .optional(),
    /**
     * Character-by-character semantic decomposition of this word.
     *
     * Each entry references the dictionary sense used to explain the
     * corresponding character's meaning within this word.
     */
    charSenses: z.array(hanziWordSchema.nullable()).optional(),
  })
  .strict();

export type HanziWordMeaning = z.infer<typeof hanziWordMeaningSchema>;
export type HanziWordWithMeaning = [HanziWord, HanziWordMeaning];

export const dictionaryJsonMutableSchema = z
  .array(z.tuple([hanziWordSchema, hanziWordMeaningSchema]))
  .transform((x) => new Map(x));

export type DictionaryJsonMutable = z.infer<typeof dictionaryJsonMutableSchema>;

export const dictionaryJsonSchema =
  dictionaryJsonMutableSchema.transform(deepReadonly);

export type DictionaryJson = z.infer<typeof dictionaryJsonSchema>;
